import { useCallback, useEffect, useState } from 'react';

const VERSION_CHECK_KEY = 'app-version';
const VERSION_CHECK_INTERVAL = 5 * 60 * 1000; // 5 minutes

/**
 * Get the current app version from the meta tag in index.html
 */
function getCurrentVersion(): string {
  const metaTag = document.querySelector('meta[name="app-version"]');
  const version = metaTag?.getAttribute('content') || import.meta.env.VITE_BUILD_VERSION || '';

  // In development, use a stable daily version to avoid false positives
  if (import.meta.env.DEV && (!version || version.includes('-'))) {
    const today = new Date();
    const dateStr = today.toISOString().split('T')[0]; // YYYY-MM-DD
    return `dev-${dateStr}`;
  }

  return version || 'unknown';
}

/**
 * Get the stored version from localStorage
 */
function getStoredVersion(): string {
  return localStorage.getItem(VERSION_CHECK_KEY) || '';
}

/**
 * Store the current version in localStorage
 */
function storeCurrentVersion(version: string) {
  localStorage.setItem(VERSION_CHECK_KEY, version);
}

/**
 * Check if a new version is available
 */
function hasNewVersion(): boolean {
  const current = getCurrentVersion();
  const stored = getStoredVersion();

  // No version info available
  if (current === 'unknown' || !current) return false;

  // Skip version check in development mode (hot reload causes false positives)
  if (import.meta.env.DEV) return false;

  // First visit - store current version and don't show update
  if (!stored) {
    storeCurrentVersion(current);
    return false;
  }

  // Version matches - no update needed
  if (current === stored) return false;

  // If current version looks like a timestamp-based fallback (starts with timestamp-like pattern),
  // and stored version is also a fallback, don't trigger update (prevents false positives)
  const timestampPattern = /^[a-z0-9]{8,}-[a-z0-9]{6,}$/i;
  if (timestampPattern.test(current) && timestampPattern.test(stored)) {
    // Both are fallback versions - likely false positive, don't update
    // But update stored version to current to prevent repeated checks
    storeCurrentVersion(current);
    return false;
  }

  // New version available!
  return true;
}

export interface UseVersionCheckReturn {
  hasUpdate: boolean;
  refresh: () => void;
}

/**
 * Hook to check for new app version
 *
 * Strategy:
 * 1. Check on mount (initial load)
 * 2. Check when window visibility changes (user returns to tab)
 * 3. Periodic polling fallback (every 5 minutes)
 * 4. SKIPPED in development mode to avoid hot reload false positives
 *
 * After refresh, the stored version matches current version, so banner disappears.
 */
export function useVersionCheck(): UseVersionCheckReturn {
  const [hasUpdate, setHasUpdate] = useState(false);

  const checkVersion = useCallback(() => {
    // Skip in development
    if (import.meta.env.DEV) return;

    if (hasNewVersion()) {
      setHasUpdate(true);
    }
  }, []);

  const refresh = useCallback(() => {
    // Force hard reload by adding cache-busting timestamp
    const url = new URL(window.location.href);
    url.searchParams.set('_v', Date.now().toString());
    window.location.href = url.toString();
  }, []);

  useEffect(() => {
    // Skip version checking in development
    if (import.meta.env.DEV) {
      const current = getCurrentVersion();
      if (current !== 'unknown') {
        storeCurrentVersion(current);
      }
      return;
    }

    // CRITICAL: Store current version IMMEDIATELY on mount before any checks
    // This prevents false positives where the version check runs before version is stored
    const current = getCurrentVersion();
    let shouldSkipInitialCheck = false;

    if (current !== 'unknown') {
      const stored = getStoredVersion();

      // If page was refreshed (has _v param), update stored version immediately
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.has('_v')) {
        storeCurrentVersion(current);
        // Remove the _v param from URL for cleaner URLs
        urlParams.delete('_v');
        const newUrl =
          window.location.pathname +
          (urlParams.toString() ? `?${urlParams.toString()}` : '') +
          window.location.hash;
        window.history.replaceState({}, '', newUrl);
        // Don't check after refresh - we just updated to current version
        shouldSkipInitialCheck = true;
      } else if (!stored) {
        // Store current version if we don't have one stored yet
        storeCurrentVersion(current);
        // Don't check immediately after storing on first load
        shouldSkipInitialCheck = true;
      }
    }

    // Check when window becomes visible (user switches back to tab)
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        // Re-check version when user returns to tab
        checkVersion();
      }
    };

    // Small delay before first check to ensure DOM is fully ready and version is stable
    // Skip initial check if we just stored the version (first load or after refresh)
    const timeoutId = shouldSkipInitialCheck
      ? null
      : setTimeout(() => {
          checkVersion();
        }, 500);

    // Periodic polling fallback
    const intervalId = setInterval(checkVersion, VERSION_CHECK_INTERVAL);

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearInterval(intervalId);
    };
  }, [checkVersion]);

  return {
    hasUpdate,
    refresh,
  };
}
